/**
 * Headlines API
 *
 * API functions for the Headlines feature.
 * Transforms camelCase responses from backend to snake_case internal types.
 */

import { api } from '@/lib/api';
import type {
  Headline,
  HeadlinesResponse,
  CreateHeadlineInput,
  UpdateHeadlineInput,
  HeadlineFilters,
} from './types';

// =============================================================================
// API Response Types (camelCase from backend)
// =============================================================================

interface ApiHeadline {
  id: string;
  title: string;
  body: string | null;
  type: string;
  teamIds: string[] | null;
  ownerId: string | null;
  ownerName: string | null;
  createdBy: string;
  createdByName: string | null;
  isArchived: boolean;
  scheduledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiHeadlinesResponse {
  headlines: ApiHeadline[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

// =============================================================================
// Transform Functions
// =============================================================================

function transformHeadline(data: ApiHeadline): Headline {
  return {
    id: data.id,
    title: data.title,
    body: data.body,
    type: data.type as Headline['type'],
    team_ids: Array.isArray(data.teamIds) ? data.teamIds : [],
    owner_id: data.ownerId ?? null,
    owner_name: data.ownerName ?? null,
    created_by: data.createdBy,
    created_by_name: data.createdByName,
    is_archived: data.isArchived,
    scheduled_at: data.scheduledAt ?? null,
    created_at: data.createdAt,
    updated_at: data.updatedAt,
  };
}

function transformHeadlinesResponse(data: ApiHeadlinesResponse): HeadlinesResponse {
  return {
    headlines: data.headlines.map(transformHeadline),
    page_number: data.pageNumber,
    page_length: data.pageLength,
    total: data.total,
  };
}

// =============================================================================
// Helper: Build Query Params
// =============================================================================

function buildQueryParams(filters?: HeadlineFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (filters?.type) params.type = filters.type;
  if (filters?.team_ids && filters.team_ids.length > 0) params.teamIds = filters.team_ids.join(',');
  if (filters?.search) params.search = filters.search;
  if (filters?.include_archived) params.includeArchived = 'true';
  if (filters?.scheduled) params.scheduled = filters.scheduled;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;
  return params;
}

// =============================================================================
// API Functions
// =============================================================================

export async function getHeadlines(filters?: HeadlineFilters): Promise<HeadlinesResponse> {
  const params = buildQueryParams(filters);
  const data = await api.get<ApiHeadlinesResponse>('/v4/headlines', params);
  return transformHeadlinesResponse(data);
}

export async function getHeadline(id: string): Promise<Headline> {
  const data = await api.get<ApiHeadline>(`/v4/headlines/${id}`);
  return transformHeadline(data);
}

export async function createHeadline(input: CreateHeadlineInput): Promise<Headline> {
  const body: Record<string, unknown> = {
    title: input.title,
    type: input.type,
    teamIds: input.team_ids,
  };
  if (input.body !== undefined) body.body = input.body;
  if (input.owner_id !== undefined) body.ownerId = input.owner_id;
  if (input.owner_name !== undefined) body.ownerName = input.owner_name;
  if (input.scheduled_at !== undefined) body.scheduledAt = input.scheduled_at;
  const data = await api.post<ApiHeadline>('/v4/headlines', body);
  return transformHeadline(data);
}

export async function updateHeadline(id: string, input: UpdateHeadlineInput): Promise<Headline> {
  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body.title = input.title;
  if (input.body !== undefined) body.body = input.body;
  if (input.type !== undefined) body.type = input.type;
  if (input.team_ids !== undefined) body.teamIds = input.team_ids;
  if (input.owner_id !== undefined) body.ownerId = input.owner_id;
  if (input.owner_name !== undefined) body.ownerName = input.owner_name;
  if (input.scheduled_at !== undefined) body.scheduledAt = input.scheduled_at;
  const data = await api.put<ApiHeadline>(`/v4/headlines/${id}`, body);
  return transformHeadline(data);
}

export async function archiveHeadline(id: string): Promise<void> {
  await api.delete(`/v4/headlines/${id}`);
}

export async function unarchiveHeadline(id: string): Promise<Headline> {
  const data = await api.post<ApiHeadline>(`/v4/headlines/${id}/unarchive`, {});
  return transformHeadline(data);
}
